// frontend/src/pages/conta/MinhaContaPage.jsx
import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';

function MinhaContaPage() {
  const { user: authUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [groupedPermissions, setGroupedPermissions] = useState([]);
  const [passwordHelp, setPasswordHelp] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Ícones para cada módulo
  const moduleIcons = {
    'dashboard': 'bi-speedometer2',
    'adolescentes': 'bi-people',
    'usuarios': 'bi-person-badge',
    'atendimentos': 'bi-clipboard-check',
    'configuracoes': 'bi-gear',
    'relatorios': 'bi-pie-chart',
    'administracao': 'bi-shield-lock',
    'especialistas': 'bi-person-video3'
  };

  // Nomes amigáveis para módulos
  const moduleNames = {
    'dashboard': 'Painel',
    'adolescentes': 'Adolescentes',
    'usuarios': 'Usuários',
    'atendimentos': 'Atendimentos',
    'configuracoes': 'Configurações',
    'relatorios': 'Relatórios',
    'administracao': 'Administração',
    'especialistas': 'Especialistas'
  };

  // Estado do formulário de senha
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // Carregar dados da conta
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await api.get('/minha-conta');
        setUserData(response.data.user);
        setPermissions(response.data.permissions);
        setPasswordHelp(response.data.password_help);

        // Agrupar permissões por módulo
        const grouped = {};
        response.data.permissions.forEach(perm => {
          const module = perm.module || 'geral';
          if (!grouped[module]) {
            grouped[module] = [];
          }
          grouped[module].push(perm);
        });

        // Converter para array e ordenar
        const groupedArray = Object.entries(grouped)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([module, perms]) => ({ module, permissions: perms }));
        
        setGroupedPermissions(groupedArray);
      } catch (err) {
        setError('Erro ao carregar dados da conta.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Submeter alteração de senha
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError(null);
    setPasswordSuccess(false);

    // Validações frontend
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Todos os campos são obrigatórios.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('A nova senha e a confirmação não coincidem.');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('A senha deve ter no mínimo 8 caracteres.');
      return;
    }

    if (!/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#?&]+$/.test(newPassword)) {
      setPasswordError('A senha deve conter letras e números.');
      return;
    }

    try {
      setChangingPassword(true);
      await api.put('/minha-conta/senha', {
        current_password: currentPassword,
        password: newPassword,
        password_confirmation: confirmPassword,
      });

      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // Limpar mensagem de sucesso após 5 segundos
      setTimeout(() => setPasswordSuccess(false), 5000);
    } catch (err) {
      const message = err.response?.data?.message || 'Erro ao alterar senha.';
      const errors = err.response?.data?.errors;

      if (errors) {
        const firstError = Object.values(errors)[0];
        setPasswordError(Array.isArray(firstError) ? firstError[0] : firstError);
      } else {
        setPasswordError(message);
      }
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) return <LoadingSpinner text="Carregando dados da conta..." />;

  if (error) {
    return (
      <div className="alert alert-danger">
        <i className="bi bi-exclamation-triangle me-2"></i>
        {error}
      </div>
    );
  }

  // Verifica se é usuário LDAP (não pode alterar senha)
  const isLdapUser = userData?.email?.endsWith('@sejus.df.gov.br');

  return (
    <>
      <div className="content-header">
        <h1 className="content-title">Minha Conta</h1>
        <p className="content-subtitle">Informações pessoais e configurações de segurança</p>
      </div>

      <div className="row g-4">
        {/* Card de Informações Pessoais */}
        <div className={isLdapUser ? "col-12" : "col-lg-6"}>
          <div className="card h-100">
            <div className="card-header">
              <h5 className="card-title mb-0">
                <i className="bi bi-person-circle me-2"></i>
                Informações Pessoais
              </h5>
            </div>
            <div className="card-body">
              <div className="mb-3">
                <label className="form-label text-secondary">Nome</label>
                <p className="fw-bold">{userData?.name}</p>
              </div>
              <div className="mb-3">
                <label className="form-label text-secondary">Email</label>
                <p className="fw-bold">{userData?.email}</p>
              </div>
              <div className="mb-3">
                <label className="form-label text-secondary">Órgão</label>
                <p className="fw-bold">
                  {userData?.orgao?.nome} ({userData?.orgao?.sigla})
                </p>
              </div>
              <div className="mb-3">
                <label className="form-label text-secondary">Perfil</label>
                <p className="fw-bold">
                  {userData?.perfil?.nome}{' '}
                  <span className="badge bg-info">
                    {userData?.perfil?.tipo_acesso}
                  </span>
                </p>
              </div>
              <div className="mb-0">
                <label className="form-label text-secondary">Membro desde</label>
                <p className="fw-bold">
                  {userData?.created_at
                    ? new Date(userData.created_at).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: 'long',
                        year: 'numeric',
                      })
                    : '—'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Card de Alterar Senha - Apenas para usuários não-LDAP */}
        {!isLdapUser && (
        <div className="col-lg-6">
          <div className="card h-100">
            <div className="card-header">
              <h5 className="card-title mb-0">
                <i className="bi bi-shield-lock me-2"></i>
                Alterar Senha
              </h5>
            </div>
            <div className="card-body">
              <form onSubmit={handlePasswordChange}>
                <div className="mb-3">
                  <label htmlFor="currentPassword" className="form-label">
                    Senha Atual
                  </label>
                  <input
                    type="password"
                    className="form-control"
                    id="currentPassword"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    disabled={changingPassword}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="newPassword" className="form-label">
                    Nova Senha
                  </label>
                  <input
                    type="password"
                    className="form-control"
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    disabled={changingPassword}
                    required
                  />
                  <div className="form-text text-white">
                    <i className="bi bi-info-circle me-1"></i>
                    {passwordHelp}
                  </div>
                </div>

                <div className="mb-3">
                  <label htmlFor="confirmPassword" className="form-label">
                    Confirme a Nova Senha
                  </label>
                  <input
                    type="password"
                    className="form-control"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={changingPassword}
                    required
                  />
                </div>

                {passwordError && (
                  <div className="alert alert-danger d-flex align-items-center">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {passwordError}
                  </div>
                )}

                {passwordSuccess && (
                  <div className="alert alert-success d-flex align-items-center">
                    <i className="bi bi-check-circle me-2"></i>
                    Senha alterada com sucesso!
                  </div>
                )}

                <button
                  type="submit"
                  className="btn btn-success w-100"
                  disabled={changingPassword}
                >
                  {changingPassword ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Alterando...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-shield-check me-2"></i>
                      Alterar Senha
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
        )}

        {/* Card de Permissões */}
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">
                <i className="bi bi-shield-check me-2"></i>
                Minhas Permissões
              </h5>
            </div>
            <div className="card-body">
              {groupedPermissions.length > 0 ? (
                <div className="row g-3">
                  {groupedPermissions.map((group, index) => {
                    const icon = moduleIcons[group.module] || 'bi-gear';
                    const displayName = moduleNames[group.module] || group.module.replace(/_/g, ' ');
                    
                    return (
                      <div key={index} className="col-lg-4 col-md-6">
                        <div className="card h-100 bg-dark border-secondary">
                          <div className="card-header bg-secondary text-white">
                            <h6 className="mb-0 text-capitalize">
                              <i className={`bi ${icon} me-2`}></i>
                              {displayName}
                            </h6>
                          </div>
                          <div className="card-body">
                            <ul className="list-unstyled mb-0">
                              {group.permissions.map((perm) => (
                                <li key={perm.id} className="mb-2">
                                  <i className="bi bi-check-circle text-success me-2"></i>
                                  <small>{perm.description}</small>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-muted mb-0">
                  <i className="bi bi-info-circle me-2"></i>
                  Nenhuma permissão atribuída.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default MinhaContaPage;
